import type { ContractDocument } from "@closer/shared";

/**
 * KESİNTİSİZ METİN ⇄ MADDE YAPISI (CLOS-633).
 *
 * ### Neden var
 *
 * Kart sözleşme yazımının "her madde için ayrı kart açmak" yerine normal bir doküman
 * editöründeki gibi kesintisiz olmasını istiyor. Kanonik saklama biçimi ise ADR-0026 ile
 * yapısal: `{ clauses: [{ id, title, blocks }] }`. İkisi çelişmiyor — çelişen şey YAZIM
 * YÜZEYİ ile SAKLAMA BİÇİMİNİ aynı şey sanmaktı.
 *
 * Bu modül ikisi arasındaki köprü: kullanıcı düz yazıyor, sistem yapıya çeviriyor. HTML
 * saklama, ofset tabanlı işaretler ve yeni bir editör bağımlılığı hâlâ yok; ADR-0026'nın
 * gerekçeleri (temizleme borcu, karşılaştırma, madde kütüphanesi) olduğu gibi duruyor.
 *
 * ### Sezgi BASİT ve öyle kalmalı
 *
 * Boş satır maddeyi ayırır, ilk satır başlık olur. Şablon oluşturma ekranı da 11
 * Ağustos'tan beri aynı kuralı kullanıyor; oradaki yorumun gerekçesi burada da geçerli:
 * numara sezen ya da başlık tahmin eden bir ayrıştırıcı, kullanıcının yazdığından farklı
 * bir yapı üretir ve kullanıcı o farkı geç fark eder.
 *
 * ### ⚠ Gidiş-dönüş KAYIPSIZ olmak zorunda
 *
 * Ekran belgeyi metne çevirip gösteriyor, kullanıcı düzenliyor, tekrar yapıya çeviriyoruz.
 * Dönüşüm kayıplı olsaydı hiç dokunulmayan bir madde bile her açılışta biraz değişir ve
 * `[095]` karşılaştırması olmayan bir fark gösterirdi. `metneCevir` ile `maddelereBol`
 * birbirinin tersi; testler bunu iki yönde de ölçüyor.
 */

/**
 * MADDE İŞARETİ METİNDE `- ` İLE TAŞINIYOR (CLOS-633: *"temel biçimlendirme belge
 * üzerinde doğrudan uygulanır"*).
 *
 * ⚠ Taşınmasaydı gidiş-dönüş KAYIPLI olurdu: madde yüzeyinde işaretli yazılmış bir satır
 * belge yüzeyinden bir kez geçtiğinde sessizce düz paragrafa dönerdi. Kullanıcı hiçbir şeye
 * dokunmadan ekranı açıp kapattığında belgesi değişmiş olurdu.
 *
 * ⚠ İşaret metnin İÇİNDE saklanmıyor, yalnız gösterimde eklenip okunuyor: kanonik model
 * hâlâ `{ kind, text }` (ADR-0026) ve `text` düz metin.
 */
const ISARET = "- ";

function blokMetni(blok: ContractDocument["clauses"][number]["blocks"][number]): string {
  return blok.kind === "bullet" ? `${ISARET}${blok.text}` : blok.text;
}

/** Bir maddeyi metne çevirir: başlık ilk satır, bloklar altına. */
function maddeMetni(madde: ContractDocument["clauses"][number]): string {
  const govde = madde.blocks.map(blokMetni).join("\n");
  if (!madde.title) return govde;
  return govde ? `${madde.title}\n${govde}` : madde.title;
}

/** Belgeyi kesintisiz metne çevirir; maddeler arasında BOŞ SATIR ayracı. */
export function belgeyiMetneCevir(belge: ContractDocument): string {
  return belge.clauses.map(maddeMetni).join("\n\n");
}

/**
 * Kesintisiz metni madde yapısına çevirir.
 *
 * ⚠ `kimlik` DIŞARIDAN geliyor. İçeride `crypto.randomUUID()` çağırsaydık kullanıcı her
 * tuşa bastığında bütün maddeler yeni kimlik alır; kaydetme sırasında `[095]` her maddeyi
 * "silinmiş + eklenmiş" görürdü. Çağıran, mevcut belgenin kimliklerini SIRAYLA yeniden
 * kullanıyor — böylece düzenlenmemiş maddeler kimliğini koruyor.
 */
export function metniMaddelereBol(
  metin: string,
  kimlik: (index: number) => string,
): ContractDocument {
  const clauses = metin
    .split(/\n\s*\n/)
    .map((parca) => parca.replace(/\s+$/, ""))
    .filter((parca) => parca.trim().length > 0)
    .map((parca, index) => {
      const satirlar = parca.split("\n");
      const baslik = satirlar[0]!.trim();
      const govdeSatirlari = satirlar.slice(1);

      /**
       * ⚠ İşaretli satırlar AYRI blok, düz satırlar tek paragrafta toplanıyor.
       *
       * Her satırı ayrı paragraf yapsaydık, kullanıcının tek bir paragraf olarak yazdığı
       * çok satırlı metin madde yüzeyinde parçalanmış görünürdü; hepsini tek bloğa
       * koysaydık madde işareti kaybolurdu.
       */
      const blocks: ContractDocument["clauses"][number]["blocks"] = [];
      let paragraf: string[] = [];
      const paragrafiKapat = () => {
        const metin = paragraf.join("\n").replace(/\s+$/, "");
        if (metin) blocks.push({ kind: "paragraph", text: metin });
        paragraf = [];
      };

      for (const satir of govdeSatirlari) {
        if (satir.trimStart().startsWith(ISARET)) {
          paragrafiKapat();
          blocks.push({ kind: "bullet", text: satir.trimStart().slice(ISARET.length).trimEnd() });
        } else {
          paragraf.push(satir);
        }
      }
      paragrafiKapat();

      return { id: kimlik(index), title: baslik, blocks };
    });

  return { clauses };
}

/**
 * Mevcut belgenin kimliklerini sırayla veren üretici.
 *
 * Metin büyüyüp yeni madde doğduğunda liste tükeniyor; o noktada yeni kimlik üretiliyor.
 * Sıra tabanlı eşleme kusursuz değil (araya madde eklenirse sonrakiler kayar) ama
 * alternatifi her kaydetmede TÜM belgeyi yeniden doğurmak olurdu.
 */
export function kimlikUretici(belge: ContractDocument): (index: number) => string {
  const mevcut = belge.clauses.map((c) => c.id);
  return (index) => mevcut[index] ?? crypto.randomUUID();
}

/**
 * GÖNDERİLEBİLİR belge: boş bloklar atılıyor.
 *
 * ⚠ Şema boş metinli bloğu REDDEDİYOR. Kullanıcı iki boş satır bırakıp yazmaya devam
 * ettiğinde bölme kuralı boş bir blok üretebiliyor ve istek 422'ye düşüyordu; şablon
 * ekranlarında bu hata hiçbir alana bağlanamadığı için ekranda HİÇ görünmüyordu — kullanıcı
 * Kaydet'e basıyor, hiçbir şey olmuyordu (kusur avı, 26 Ağustos).
 *
 * Sözleşme editöründe aynı süzgeç zaten vardı; buraya çıkarıldı ki şablon oluşturma ve
 * düzenleme ekranları da aynı kuralı kullansın — üç kopya gün gelip ayrışırdı.
 */
export function gonderilebilirBelge(belge: ContractDocument): ContractDocument {
  return {
    clauses: belge.clauses.map((madde) => ({
      ...madde,
      blocks: madde.blocks.filter((blok) => blok.text.length > 0),
    })),
  };
}
